import React from 'react';

export type SortOrder = 'ASC' | 'DESC';

export type UseSortFilterStateOptions = {
    initialSortBy?: string;
    initialSortOrder?: SortOrder;
    initialFilterList?: string;
    /**
     * When the user picks this filter value, sort options are reset
     * back to their initial values. Defaults to `'all'`. Set to
     * `null` to disable the reset behaviour.
     */
    resetFilterValue?: string | null;
    /**
     * Optional callback invoked whenever a reset is triggered (i.e.
     * the user picked the `resetFilterValue`). Use this to also
     * clear consumer-owned state such as the search input.
     */
    onReset?: () => void;
};

export type UseSortFilterStateReturn = {
    sortBy: string;
    sortOrder: SortOrder;
    filterList: string;
    /**
     * Bumped every time the user picks a sort option, even when the
     * value is unchanged. Consumers (e.g. `RealmHierarchy`) use this
     * counter to detect sort clicks reliably (React's `useState`
     * would otherwise bail out on identical values).
     */
    sortOnce: number;
    /** Same idea as `sortOnce`, but for filter clicks. */
    filterOnce: number;
    setSortBy: (value: string) => void;
    setSortOrder: (value: SortOrder) => void;
    setFilterList: (value: string) => void;
};

/**
 * Encapsulates the "search / sort / filter" panel state that both
 * `router/drawer.tsx` and `pages/RealmList/RealmList.tsx` need to
 * share when driving `<RealmHierarchy />`.
 *
 * The wrapped setters bump dedicated counters (`sortOnce` /
 * `filterOnce`) on every user click so downstream components can
 * distinguish a sort click from a filter click — even when the value
 * being selected is the same as the current one.
 */
export const useSortFilterState = (options: UseSortFilterStateOptions = {}): UseSortFilterStateReturn => {
    const {
        initialSortBy = 'display_name',
        initialSortOrder = 'ASC',
        initialFilterList = 'all',
        resetFilterValue = 'all',
        onReset,
    } = options;

    const [sortBy, setSortByState] = React.useState<string>(initialSortBy);
    const [sortOrder, setSortOrderState] = React.useState<SortOrder>(initialSortOrder);
    const [filterList, setFilterListState] = React.useState<string>(initialFilterList);

    const [sortOnce, setSortOnce] = React.useState<number>(0);
    const [filterOnce, setFilterOnce] = React.useState<number>(0);

    // Keep latest values in refs so `setFilterList` stays stable
    // while still being able to invoke the freshest `onReset` and
    // use the latest initial values.
    const onResetRef = React.useRef(onReset);
    const initialSortByRef = React.useRef(initialSortBy);
    const initialSortOrderRef = React.useRef(initialSortOrder);
    React.useEffect(() => {
        onResetRef.current = onReset;
        initialSortByRef.current = initialSortBy;
        initialSortOrderRef.current = initialSortOrder;
    }, [onReset, initialSortBy, initialSortOrder]);

    const bumpSort = React.useCallback((): void => {
        setSortOnce((n) => n + 1);
    }, []);
    const bumpFilter = React.useCallback((): void => {
        setFilterOnce((n) => n + 1);
    }, []);

    const setSortBy = React.useCallback(
        (value: string): void => {
            bumpSort();
            setSortByState(value);
        },
        [bumpSort]
    );
    const setSortOrder = React.useCallback(
        (value: SortOrder): void => {
            bumpSort();
            setSortOrderState(value);
        },
        [bumpSort]
    );
    const setFilterList = React.useCallback(
        (value: string): void => {
            bumpFilter();
            setFilterListState(value);
            // Reset sort options (and notify consumer) when the user
            // picks the configured reset value (e.g. "Display All").
            if (resetFilterValue !== null && value === resetFilterValue) {
                setSortByState(initialSortByRef.current);
                setSortOrderState(initialSortOrderRef.current);
                onResetRef.current?.();
            }
        },
        [bumpFilter, resetFilterValue]
    );

    return {
        sortBy,
        sortOrder,
        filterList,
        sortOnce,
        filterOnce,
        setSortBy,
        setSortOrder,
        setFilterList,
    };
};
